import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { useArrayContext } from "../../../state/ArrayContext";
import { useStoredItem } from "../../../state/InternalContext";
import { SourceItem } from "../../../shared/models/source-item.model";
import { TextExtractor, findFirstComponentOfType } from "../../internal/TextExtractor";

interface ItemPropertyValuesProps {
    item: string,
    category: string,
    propertyNameArg?: string | null,
    children?: ReactNode[],
}

/***
 * Array source that returns the values of a property of a stored item.
 * 
 * Params:
 *      item: The name of the stored item
 *      category: The category of the property
 *      property: The name of the property
 */
export const ItemPropertyValues = React.memo(function ItemPropertyValues({ item, category, propertyNameArg, children }: ItemPropertyValuesProps): React.ReactElement {
    const { setArray } = useArrayContext();
    const storedItem = useStoredItem(item);

    // Get property value from the property child unless it was provided as a prop
    const [propertyValue, setPropertyValue] = useState("");
    const propertyChild = useMemo(() => propertyNameArg ? null : findFirstComponentOfType(children, "arg-property"), [children, propertyNameArg]);

    useEffect(() => {
        const usedPropertyName = propertyNameArg || propertyValue;
        if (usedPropertyName) {
            const props = storedItem?.props?.[category]?.[usedPropertyName] || [];
            const result: Array<SourceItem> = props?.map((value, ix) => ({
                value: value,
                props: {},
                position: ix + 1,
            })) || [];
            setArray(result);
        }
    }, [storedItem, category, propertyValue, propertyNameArg, setArray]);

    return <>
        {propertyChild && (
            <TextExtractor onExtractedText={setPropertyValue}>
                {propertyChild?.props.children}
            </TextExtractor>
        )}
    </>;
});